import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import GenericFormDrawer from '../../../components/GenericFormDrawer';
import api from '../../../services/api';
import { positionOptionLabel } from '../../positions/positionPicker';

// Org şemasında "Kök / Alt Pozisyon Ekle" (2026-09-30).
//
// Eski hâli bir MODAL'dı (ürün kuralı: veri girişi = GenericFormDrawer) ve İLK alanı boş bir
// "Pozisyon Kodu" kutusuydu; altındaki küçük "Boş Bırakılırsa Otomatik Üretilir" notu fark
// edilmiyor, müşteri pozisyona hangi kodu vereceğini bilemeyip takılıyordu. Workday /
// SuccessFactors'ta olduğu gibi kod artık SORULMAZ: kayıtta sunucu sıradaki kodu atar
// (PositionDataAccess.ResolveCode → POS-0042); kendi kodlama düzeni olan şirket "Kodu kendim
// gireceğim" ile alanı açar. Alan sırası müşterinin düşündüğü sıradır: birim, ünvan, kod.
//
// Departman, tıklanan üst kartın departmanıyla ÖNCEDEN dolar — alt pozisyonların çoğu üstüyle
// aynı birimdedir; farklıysa kullanıcı değiştirir.
//
// `parentPosition`: org şeması düğüm verisi (positionId, code, titleName, departmentName,
// departmentId, userName) — kök eklemede null.
export default function AddPositionDrawer({ isOpen, parentPosition, onClose, onSuccess }) {
  const { t } = useTranslation();
  const [departments, setDepartments] = useState([]);
  const [titles, setTitles] = useState([]);

  useEffect(() => {
    if (!isOpen) return;
    // GET istekleri reddedilmez (interceptor) — veri yoksa boş liste.
    api.get('/v1/departments?pageSize=500').then((res) => {
      const r = res.data?.data;
      const items = Array.isArray(r) ? r : (r?.data || []);
      setDepartments(items.map((d) => ({ id: d.id, name: d.name })));
    });
    api.get('/v1/titles?pageSize=500').then((res) => {
      const r = res.data?.data;
      const items = Array.isArray(r) ? r : (r?.data || []);
      setTitles(items.map((x) => ({ id: x.id, name: x.name })));
    });
  }, [isOpen]);

  const isChild = !!parentPosition?.positionId;
  const parentLabel = isChild
    ? positionOptionLabel({
      titleName: parentPosition.titleName,
      departmentName: parentPosition.departmentName,
      occupantName: parentPosition.userName,
      code: parentPosition.code,
    })
    : '';

  const schema = useMemo(() => [
    {
      name: 'parentNotice', type: 'notice', grid: 12,
      content: `${t('position.parentPosition')}: ${parentLabel}`,
      hidden: !isChild,
    },
    {
      name: 'departmentId', label: t('position.departmentName'), type: 'select', grid: 12,
      options: departments, idKey: 'id', labelKey: 'name',
      defaultValue: parentPosition?.departmentId || undefined,
      description: isChild ? t('orgChart.departmentFromParentHint') : undefined,
    },
    {
      name: 'titleId', label: t('position.titleName'), type: 'select', grid: 12,
      options: titles, idKey: 'id', labelKey: 'name',
    },
    {
      name: 'codeAutoNotice', type: 'notice', grid: 12,
      content: t('position.codeAutoNotice'),
      hidden: (fd) => !!fd.manualCode,
    },
    { name: 'manualCode', label: t('position.codeManualToggle'), type: 'checkbox', grid: 12 },
    {
      // Zorunlu DEĞİL: kutucuk işaretli ama alan boş kalırsa sunucu yine kod üretir.
      name: 'code', label: t('position.code'), type: 'text', grid: 12,
      hidden: (fd) => !fd.manualCode,
      description: t('position.codeManualHint'),
    },
  ], [t, departments, titles, isChild, parentLabel, parentPosition]);

  const handleSave = useCallback(async (fd) => {
    try {
      const res = await api.post('/v1/positions', {
        code: fd.manualCode ? ((fd.code || '').trim() || undefined) : undefined,
        departmentId: fd.departmentId || undefined,
        titleId: fd.titleId || undefined,
        parentPositionId: parentPosition?.positionId || undefined,
      });
      // PositionResponse yukarı taşınır: sayfa yeni kartın id'siyle kamerayı ona odaklar.
      onSuccess?.(res.data?.data);
      return true;
    } catch {
      return false; // interceptor mesajı gösterdi; çekmece açık kalır
    }
  }, [parentPosition, onSuccess]);

  return (
    <GenericFormDrawer
      open={isOpen}
      onClose={onClose}
      title={isChild ? t('orgChart.addChild') : t('orgChart.addRoot')}
      schema={schema}
      initialValues={null}
      onSave={handleSave}
    />
  );
}
